/* assets/bundle-upsell.js
 * Bundle upsell block: one opt-in for the whole set, colour matching,
 * variant dropdowns, bundle price (with discount) in the add to cart button,
 * adding the main product + every upsell to the cart together, and an
 * optional popup offering the set when Add to cart is clicked without it.
 */
(() => {
  if (customElements.get('bundle-upsell')) return;

  const log = (...args) => {
    try {
      console.info('[Bundle upsell]', ...args);
    } catch (e) {
      /* no console */
    }
  };
  const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();
  const root = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  class BundleUpsell extends HTMLElement {
    connectedCallback() {
      if (!this._ready) this.init();
      else {
        if (this.modal && !this.modal.isConnected) document.body.appendChild(this.modal);
        (this.charts || []).forEach((d) => {
          if (!d.isConnected) document.body.appendChild(d);
        });
      }
      this.start();
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      // The popup lives on <body>; remove it with the block (e.g. theme editor re-renders).
      if (this.modal) {
        if (this.modal.open) this.modal.close();
        this.restoreList();
        this.modal.remove();
      }
      (this.charts || []).forEach((d) => {
        if (d.open) d.close();
        d.remove();
      });
    }

    init() {
      this._ready = true;
      this.section = this.closest('.shopify-section') || document.body;
      this.colorNames = (this.dataset.colorNames || 'color,colour')
        .split(',')
        .map(norm)
        .filter(Boolean);
      this.matchColor = this.dataset.matchColor === 'true';
      this.matchQuantity = this.dataset.matchQuantity === 'true';
      this.afterAdd = this.dataset.afterAdd || 'cart';
      this.moneyFormat = (this.dataset.moneyFormat || '{{amount}}').replace(/<[^>]*>/g, '');
      this.discount = parseInt(this.dataset.discount, 10) || 0;

      const main = JSON.parse(this.querySelector('[data-bundle-main]').textContent);
      this.mainOptions = main.options || [];
      this.mainVariants = main.variants || [];
      this.mainIds = new Set(this.mainVariants.map((v) => String(v.id)));
      this.mainColorIndex = this.mainOptions.findIndex((o) => this.colorNames.includes(norm(o)));

      this.totalWrap = this.querySelector('[data-bundle-total-wrap]');
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.messageEl = this.querySelector('[data-bundle-message]');
      this.templates = Array.from(this.querySelectorAll('[data-bundle-template]'));
      this.checkbox = this.querySelector('[data-bundle-checkbox]');
      this.items = Array.from(this.querySelectorAll('[data-bundle-item]')).map((el) =>
        this.setupItem(el)
      );

      if (this.checkbox) {
        this.checkbox.addEventListener('change', () => {
          this.showMessage('');
          this.updateTotal();
        });
      }

      this.display = this.dataset.display || 'both';
      this.productTitle = this.dataset.productTitle || '';
      this.list = this.querySelector('[data-bundle-list]');
      if (this.list) {
        this.listHome = this.list.parentNode;
        this.listNext = this.list.nextSibling;
      }
      this.setupModal();
      this.setupCharts();

      this.sync = this.sync.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);

      // Capture phase on the section so we run before the theme's own add-to-cart handlers.
      this.section.addEventListener('click', this.handleClick, true);
      this.section.addEventListener('submit', this.handleSubmit, true);
      this.section.addEventListener('change', () => setTimeout(this.sync, 60));

      this.sync(true);
    }

    start() {
      clearInterval(this.timer);
      // Themes often update the variant id input without firing events, so poll lightly.
      this.timer = setInterval(this.sync, 400);
    }

    /* ---------- main product ---------- */

    isMainForm(form) {
      const input = form && form.querySelector('[name="id"]');
      return !!input && this.mainIds.has(String(input.value));
    }

    getMainForms() {
      return Array.from(this.section.querySelectorAll('form[action*="/cart/add"]')).filter(
        (f) => !this.contains(f) && this.isMainForm(f)
      );
    }

    getMainVariantId() {
      const form = this.getMainForms()[0];
      if (form) return String(form.querySelector('[name="id"]').value);
      const param = new URLSearchParams(window.location.search).get('variant');
      if (param && this.mainIds.has(param)) return param;
      const v = this.mainVariants.find((x) => x.available) || this.mainVariants[0];
      return v ? String(v.id) : null;
    }

    getQuantity(form) {
      let input = form && form.querySelector('[name="quantity"]');
      if (!input && form && form.id) {
        input = document.querySelector(`[name="quantity"][form="${CSS.escape(form.id)}"]`);
      }
      if (!input) {
        input = Array.from(this.section.querySelectorAll('[name="quantity"]')).find(
          (i) => !this.contains(i)
        );
      }
      const q = parseInt(input && input.value, 10);
      return q > 0 ? q : 1;
    }

    sync(force) {
      const id = this.getMainVariantId();
      if (force === true || id !== this.mainId) {
        this.mainId = id;
        const mv = this.mainVariants.find((v) => String(v.id) === id);
        this.items.forEach((item) => this.applyMainVariant(item, mv));
      }
      this.updateTotal();
    }

    /* ---------- upsell items ---------- */

    setupItem(el) {
      const data = JSON.parse(el.querySelector('[data-bundle-data]').textContent);
      const item = {
        el,
        data,
        selects: Array.from(el.querySelectorAll('[data-bundle-option]')),
        image: el.querySelector('[data-bundle-image]'),
        price: el.querySelector('[data-bundle-price]'),
        compare: el.querySelector('[data-bundle-compare]'),
        variantLabel: el.querySelector('[data-bundle-variant-label]'),
        status: el.querySelector('[data-bundle-status]'),
        colorIndex: (data.options || []).findIndex((o) => this.colorNames.includes(norm(o))),
        variant: null,
      };

      item.selects.forEach((select) =>
        select.addEventListener('change', () => this.setVariant(item, this.variantFromSelects(item)))
      );
      return item;
    }

    findColour(item, mv) {
      if (!this.matchColor || !mv || item.colorIndex < 0) return null;
      const itemColours = new Set(item.data.variants.map((v) => norm(v.options[item.colorIndex])));
      // Prefer the main product's colour option, then fall back to any of its option values
      // (handles products where the colour lives under a differently named option).
      const candidates = [];
      if (this.mainColorIndex > -1) candidates.push(mv.options[this.mainColorIndex]);
      candidates.push(...mv.options);
      return candidates.map(norm).find((c) => itemColours.has(c)) || null;
    }

    applyMainVariant(item, mv) {
      const colour = this.findColour(item, mv);
      let variant;

      if (item.selects.length) {
        if (colour) {
          const sel = item.selects.find((s) => +s.dataset.index === item.colorIndex);
          const opt = sel && Array.from(sel.options).find((o) => norm(o.value) === colour);
          if (opt) sel.value = opt.value;
        }
        variant = this.variantFromSelects(item);
        if (!variant || !variant.available) {
          const alt = this.pickVariant(item, colour);
          if (alt && alt.available) variant = alt;
        }
      } else {
        variant = this.pickVariant(item, colour);
      }

      this.setVariant(item, variant);
    }

    pickVariant(item, colour) {
      const all = item.data.variants;
      const sameColour = colour
        ? all.filter((v) => norm(v.options[item.colorIndex]) === colour)
        : all;
      const pool = sameColour.length ? sameColour : all;

      // Keep the other options (e.g. size) the same when only the colour changes.
      const cur = item.variant;
      if (cur && colour) {
        const keep = pool.find(
          (v) => v.available && v.options.every((o, i) => i === item.colorIndex || o === cur.options[i])
        );
        if (keep) return keep;
      }
      return pool.find((v) => v.available) || pool[0] || null;
    }

    variantFromSelects(item) {
      const chosen = {};
      item.selects.forEach((s) => (chosen[+s.dataset.index] = s.value));
      return (
        item.data.variants.find((v) =>
          v.options.every((o, i) => !(i in chosen) || o === chosen[i])
        ) || null
      );
    }

    setVariant(item, v) {
      item.variant = v;

      if (v) {
        item.selects.forEach((s) => {
          const val = v.options[+s.dataset.index];
          if (val != null) s.value = val;
        });
      }

      const img = (v && v.image) || item.data.image;
      if (item.image && img) item.image.src = img;

      if (item.price) item.price.textContent = v ? this.formatMoney(v.price) : '';
      if (item.compare) {
        item.compare.textContent =
          v && v.compare_at_price > v.price ? this.formatMoney(v.compare_at_price) : '';
      }
      if (item.variantLabel) {
        item.variantLabel.textContent = v && item.data.variants.length > 1 ? v.title : '';
      }

      const ok = !!(v && v.available);
      item.el.classList.toggle('is-unavailable', !ok);
      if (item.status) {
        item.status.textContent = ok
          ? ''
          : v
          ? this.dataset.soldOutText || 'Sold out'
          : this.dataset.unavailableText || 'Unavailable';
      }

      this.updateTotal();
    }

    /* ---------- bundle ---------- */

    // The set can only be added when every upsell has an available variant selected.
    bundleReady() {
      return (
        !!this.items &&
        this.items.length > 0 &&
        this.items.every((i) => i.variant && i.variant.available)
      );
    }

    // All upsells when the set is ticked (and available), otherwise none.
    checkedItems() {
      if (!this.checkbox || !this.checkbox.checked || !this.bundleReady()) return [];
      return this.items.slice();
    }

    // Price of one set of upsells, before and after the bundle discount.
    setPrices() {
      const full = (this.items || []).reduce((sum, i) => sum + (i.variant ? i.variant.price : 0), 0);
      const discount = Math.min(this.discount, full);
      return { full, discount, price: full - discount };
    }

    updateOptin() {
      if (!this.checkbox) return;
      const ready = this.bundleReady();
      this.checkbox.disabled = !ready;
      if (!ready && this.checkbox.checked) {
        this.checkbox.checked = false;
        this.showMessage(this.dataset.bundleUnavailableText || '');
      } else if (ready && this.messageEl && this.messageEl.textContent === this.dataset.bundleUnavailableText) {
        this.showMessage('');
      }
      this.classList.toggle('is-checked', this.checkbox.checked);

      // Fill in [price], [full_price], [saving], [total] and [product] in the texts.
      const p = this.setPrices();
      const forms = this.getMainForms();
      const qty = this.getQuantity(forms[0]);
      const upQty = this.matchQuantity ? qty : 1;
      const mv = this.mainVariants.find((v) => String(v.id) === this.mainId);
      const values = {
        price: this.formatMoney(p.price),
        full_price: this.formatMoney(p.full),
        saving: this.formatMoney(p.discount),
        total: this.formatMoney((mv ? mv.price * qty : 0) + p.price * upQty),
        full_total: this.formatMoney((mv ? mv.price * qty : 0) + p.full * upQty),
        product: this.productTitle,
      };
      this.templates.forEach((el) => {
        const text = el.dataset.bundleTemplate.replace(
          /\[(price|full_price|saving|total|full_total|product)\]/g,
          (_, key) => values[key]
        );
        if (el.textContent !== text) el.textContent = text;
      });
    }

    updateTotal() {
      this.updateOptin();
      const checked = this.checkedItems();
      const forms = this.getMainForms();
      const qty = this.getQuantity(forms[0]);
      const mv = this.mainVariants.find((v) => String(v.id) === this.mainId);
      const active = checked.length > 0 && !!mv && mv.available !== false;

      let total = 0;
      let compare = 0;
      if (active) {
        const upQty = this.matchQuantity ? qty : 1;
        total = mv.price * qty;
        compare = Math.max(mv.compare_at_price || 0, mv.price) * qty;
        checked.forEach((i) => {
          total += i.variant.price * upQty;
          compare += Math.max(i.variant.compare_at_price || 0, i.variant.price) * upQty;
        });
        // Bundle discount, once per set added.
        total -= this.setPrices().discount * upQty;
      }

      // Price inside the add to cart button.
      const totalText = active ? this.formatMoney(total) : '';
      const compareText = active && compare > total ? this.formatMoney(compare) : '';
      forms.forEach((form) => {
        form.classList.toggle('bundle-active', active);
        form.querySelectorAll('[data-bundle-btn-price]').forEach((el) => {
          if (el.textContent !== totalText) el.textContent = totalText;
        });
        form.querySelectorAll('[data-bundle-btn-compare]').forEach((el) => {
          if (el.textContent !== compareText) el.textContent = compareText;
        });
      });

      // Optional total line under the upsells.
      if (this.totalEl && this.totalWrap) {
        this.totalWrap.hidden = !active;
        if (active) this.totalEl.textContent = totalText;
      }
    }

    /* ---------- add to cart ---------- */

    handleClick(e) {
      if (this.bypass) return;
      const btn = e.target.closest && e.target.closest('button, input[type="submit"]');
      if (!btn || this.contains(btn)) return;
      const type = (btn.getAttribute('type') || 'submit').toLowerCase();
      if (type !== 'submit') return;
      const form = btn.form;
      if (!form || !this.isMainForm(form)) return;
      this.intercept(e, form, btn);
    }

    handleSubmit(e) {
      if (this.bypass) return;
      const form = e.target;
      if (!(form instanceof HTMLFormElement) || this.contains(form) || !this.isMainForm(form)) return;
      this.intercept(e, form, e.submitter);
    }

    // Set ticked -> add the bundle. Not ticked -> maybe offer it in the popup.
    // Otherwise leave the theme to add the product as normal.
    intercept(e, form, btn) {
      if (this.checkedItems().length) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.addBundle(form, btn);
      } else if (this.shouldOfferPopup()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.offerPopup(form, btn);
      }
    }

    // Let the theme handle the add to cart that we held back.
    resubmit() {
      const pending = this.pending;
      this.pending = null;
      if (!pending) return;
      const { form, btn } = pending;
      this.bypass = true;
      try {
        if (btn && btn.isConnected && !btn.disabled) btn.click();
        else if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      } finally {
        this.bypass = false;
      }
    }

    /* ---------- popup ---------- */

    setupModal() {
      this.modal = this.querySelector('[data-bundle-modal]');
      if (this.display === 'page') {
        log('popup is off ("Show the set" is set to "On the page only").');
        this.modal = null;
        return;
      }
      if (!this.modal) {
        log('popup markup not found: is snippets/bundle-upsell.liquid the latest version?');
        return;
      }
      if (typeof this.modal.showModal !== 'function') {
        log('this browser does not support popups (<dialog>).');
        this.modal = null;
        return;
      }

      // Testing helpers: the "don't show again this visit" memory is ignored in the
      // theme editor, and cleared by adding ?bundle_popup=reset to the page URL.
      try {
        const reset = new URLSearchParams(window.location.search).get('bundle_popup') === 'reset';
        if (reset || (window.Shopify && window.Shopify.designMode)) {
          window.sessionStorage.removeItem(this.dismissKey());
        }
      } catch (e) {
        /* storage unavailable */
      }
      log('popup ready.');
      this.modalSlot = this.modal.querySelector('[data-bundle-modal-slot]');
      this.modalMessage = this.modal.querySelector('[data-bundle-modal-message]');
      this.modalAdd = this.modal.querySelector('[data-bundle-modal-add]');

      // Move to <body> so the theme's layout/opacity can't affect it.
      document.body.appendChild(this.modal);

      this.modal
        .querySelectorAll('[data-bundle-modal-close]')
        .forEach((b) => b.addEventListener('click', () => this.closeModal('skip')));
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.closeModal('skip');
      });
      this.modal.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.closeModal('skip');
      });
      if (this.modalAdd) this.modalAdd.addEventListener('click', () => this.acceptModal());
    }

    /* ---------- size charts ---------- */

    setupCharts() {
      this.charts = Array.from(this.querySelectorAll('[data-bundle-chart]'));
      this.charts.forEach((dialog) => {
        if (typeof dialog.showModal !== 'function') return;
        document.body.appendChild(dialog);
        dialog
          .querySelectorAll('[data-bundle-chart-close]')
          .forEach((b) => b.addEventListener('click', () => dialog.close()));
        dialog.addEventListener('click', (e) => {
          if (e.target === dialog) dialog.close();
        });
      });
      this.querySelectorAll('[data-bundle-chart-open]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const dialog = this.charts.find((d) => d.id === btn.dataset.bundleChartOpen);
          if (dialog && typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
        });
      });
    }

    dismissKey() {
      return `bundle-upsell-dismissed:${this.dataset.blockId || ''}`;
    }

    popupDismissed() {
      if (this._dismissed) return true;
      try {
        return window.sessionStorage.getItem(this.dismissKey()) === '1';
      } catch (e) {
        return false;
      }
    }

    rememberDismiss() {
      this._dismissed = true;
      try {
        window.sessionStorage.setItem(this.dismissKey(), '1');
      } catch (e) {
        /* storage unavailable: remembered for this page only */
      }
    }

    shouldOfferPopup() {
      if (!this.modal || this.busy || this.pending) return false;
      if (!this.bundleReady()) {
        log('popup not shown: a product in the set is sold out in the selected options.');
        return false;
      }
      if (this.popupDismissed()) {
        log('popup not shown: it was already dismissed in this visit (add ?bundle_popup=reset to the URL to test again).');
        return false;
      }
      return true;
    }

    async offerPopup(form, btn) {
      this.pending = { form, btn };

      // Only offer the set the first time this product goes in the cart, and not
      // if everything in the set is already there.
      let skip = '';
      try {
        const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        const inCart = new Set((cart.items || []).map((i) => String(i.product_id)));
        if (this.dataset.productId && inCart.has(String(this.dataset.productId))) {
          skip = 'this product is already in the cart.';
        } else if (this.items.every((i) => inCart.has(String(i.data.id)))) {
          skip = 'everything in the set is already in the cart.';
        }
      } catch (e) {
        /* cart couldn't be read: offer the set anyway */
      }

      if (skip || !this.pending) {
        if (skip) log('popup not shown: ' + skip);
        this.resubmit();
        return;
      }

      if (this.list && this.modalSlot) this.modalSlot.appendChild(this.list);
      this.showMessage('');
      this.updateTotal();
      this.modal.showModal();
      if (this.modalAdd) this.modalAdd.focus();
    }

    restoreList() {
      if (!this.list || !this.listHome || this.list.parentNode === this.listHome) return;
      const next = this.listNext && this.listNext.parentNode === this.listHome ? this.listNext : null;
      this.listHome.insertBefore(this.list, next);
    }

    closeModal(result) {
      if (this.busy) return;
      if (this.modal && this.modal.open) this.modal.close();
      this.restoreList();
      this.showMessage('');
      if (result === 'skip' && this.pending) {
        this.rememberDismiss();
        this.resubmit();
      } else {
        this.pending = null;
      }
    }

    async acceptModal() {
      if (!this.pending || this.busy) return;
      if (!this.bundleReady()) {
        this.showMessage(this.dataset.bundleUnavailableText || '');
        return;
      }
      if (this.checkbox) this.checkbox.checked = true;
      this.updateTotal();

      const ok = await this.addBundle(this.pending.form, this.modalAdd);
      if (ok) {
        // Only reached when staying on the page (otherwise we're navigating away).
        if (this.display === 'popup' && this.checkbox) this.checkbox.checked = false;
        this.pending = null;
        if (this.modal.open) this.modal.close();
        this.restoreList();
        this.updateTotal();
      } else if (this.display === 'popup' && this.checkbox) {
        this.checkbox.checked = false;
      }
    }

    async addBundle(form, btn) {
      if (this.busy) return false;
      this.busy = true;
      this.showMessage('');

      const qty = this.getQuantity(form);
      const fd = new FormData(form);
      const main = { id: Number(fd.get('id')), quantity: qty };

      const props = {};
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && typeof v === 'string' && v !== '') props[m[1]] = v;
      }
      if (Object.keys(props).length) main.properties = props;
      const plan = fd.get('selling_plan');
      if (plan) main.selling_plan = Number(plan);

      const items = [
        main,
        ...this.checkedItems().map((i) => ({
          id: i.variant.id,
          quantity: this.matchQuantity ? qty : 1,
        })),
      ];

      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      }
      this.classList.add('is-loading');

      let navigating = false;
      let ok = false;
      try {
        const res = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || data.message || this.dataset.errorText);
        ok = true;

        // Give the theme's cart drawer / counter a chance to refresh.
        ['cart:refresh', 'cart:updated', 'cart:change'].forEach((name) =>
          document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: data }))
        );

        if (this.afterAdd === 'cart') {
          navigating = true;
          window.location.href = `${root()}cart`;
        } else if (this.afterAdd === 'reload') {
          navigating = true;
          window.location.reload();
        } else {
          this.showMessage(this.dataset.addedText || 'Added to cart');
        }
      } catch (err) {
        this.showMessage(err.message || this.dataset.errorText);
      } finally {
        this.busy = false;
        if (!navigating) {
          this.classList.remove('is-loading');
          if (btn) {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
          }
        }
      }
      return ok;
    }

    showMessage(text) {
      const inPopup = !!(this.modal && this.modal.open);
      [this.messageEl, inPopup ? this.modalMessage : null].forEach((el) => {
        if (!el) return;
        el.textContent = text || '';
        el.hidden = !text;
      });
      if (!inPopup && this.modalMessage) {
        this.modalMessage.textContent = '';
        this.modalMessage.hidden = true;
      }
    }

    /* ---------- money ---------- */

    formatMoney(cents) {
      const fix = (n, decimals, thousands = ',', dec = '.') => {
        const [i, f] = (n / 100).toFixed(decimals).split('.');
        return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (f ? dec + f : '');
      };
      const fmt = this.moneyFormat;
      const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      const key = match ? match[1] : 'amount';
      const map = {
        amount: () => fix(cents, 2),
        amount_no_decimals: () => fix(cents, 0),
        amount_with_comma_separator: () => fix(cents, 2, '.', ','),
        amount_no_decimals_with_comma_separator: () => fix(cents, 0, '.', ','),
        amount_with_apostrophe_separator: () => fix(cents, 2, "'", '.'),
        amount_with_space_separator: () => fix(cents, 2, ' ', ','),
      };
      const value = (map[key] || map.amount)();
      return match ? fmt.replace(match[0], value) : value;
    }
  }

  customElements.define('bundle-upsell', BundleUpsell);
})();
